import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import {
  TrendingUp,
  Users,
  BarChart3,
  Search,
  Filter,
  Smile,
  Meh,
  Angry,
  Settings,
  Server,
  Building,
  Target,
  FileText,
} from 'lucide-react';
import { ResponsivePie } from '@nivo/pie';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveCirclePacking } from '@nivo/circle-packing';
import { Card } from '../../styles/GlobalStyles';
import { StatsCard } from './StatsCard';
import desligamentosDataJson from '../../data/desligamentos-data.json';

// Configuração de tema para os gráficos
const chartTheme = {
  background: '#FFFFFF',
  textColor: '#1E293B',
  fontSize: 14,
  axis: {
    domain: {
      line: {
        stroke: '#E2E8F0',
        strokeWidth: 1,
      },
    },
    legend: {
      text: {
        fontSize: 16,
        fontWeight: 600,
        fill: '#475569',
      },
    },
    ticks: {
      line: {
        stroke: '#E2E8F0',
        strokeWidth: 1,
      },
      text: {
        fontSize: 13,
        fill: '#64748B',
        fontWeight: 500,
      },
    },
  },
  grid: {
    line: {
      stroke: '#F1F5F9',
      strokeWidth: 1,
    },
  },
  legends: {
    title: {
      text: {
        fontSize: 15,
        fontWeight: 600,
        fill: '#475569',
      },
    },
    text: {
      fontSize: 13,
      fontWeight: 500,
      fill: '#64748B',
    },
  },
  tooltip: {
    container: {
      background: '#FFFFFF',
      color: '#1E293B',
      fontSize: 14,
      borderRadius: '8px',
      boxShadow:
        '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      border: '1px solid #E2E8F0',
    },
  },
};

const DesligamentosContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`;

const ContentWrapper = styled.div`
  padding: 0;
`;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PageTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize['2xl']};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const FiltersContainer = styled.div`
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const FiltersRow = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FilterSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  background-color: white;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}10;
  }

  option {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  align-items: center;
`;

const CheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  user-select: none;
`;

const FilterActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border: 1px solid
    ${({ variant, theme }) =>
      variant === 'primary' ? theme.colors.primary : theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ variant, theme }) =>
    variant === 'primary' ? 'white' : theme.colors.text.secondary};
  background-color: ${({ variant, theme }) =>
    variant === 'primary' ? theme.colors.primary : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ variant, theme }) =>
      variant === 'primary'
        ? theme.colors.primaryDark
        : theme.colors.surfaceLight};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChartCard = styled(Card)`
  height: 650px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 600px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 550px;
  }
`;

const LargeChartCard = styled(ChartCard)`
  height: 600px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 550px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 500px;
  }
`;

const ExtraLargeChartCard = styled(ChartCard)`
  height: 800px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 700px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 600px;
  }
`;

const ChartTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg}
    0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  min-height: 300px;
  padding: 1.5rem;
  position: relative;
  z-index: 2;

  /* Estilos para controlar overflow de texto nos labels do Circle Packing */
  .nivo-circle-packing text {
    font-weight: 600;
    text-anchor: middle;
    font-size: 11px !important;
  }

  /* Responsividade para gráficos de pizza */
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 1rem;

    .nivo-pie .legends text {
      font-size: 12px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.5rem;
    min-height: 250px;

    .nivo-pie .legends text {
      font-size: 11px;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.5rem;
    min-height: 200px;

    .nivo-pie .legends text {
      font-size: 10px;
    }
  }
`;

const MainLayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const RightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const StatsSubGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: ${({ theme }) => theme.spacing.sm};
  }
`;

const BottomChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.md};
    margin-bottom: ${({ theme }) => theme.spacing.lg};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.sm};
    margin-bottom: ${({ theme }) => theme.spacing.md};
  }
`;

const CausesChartContainer = styled.div`
  grid-column: 1 / -1;
`;

// Estilos para a seção eNPS
const NPSSection = styled.div`
  display: grid;
  grid-template-columns: calc(70% - 1rem) calc(30% - 1rem);
  gap: 2rem;
  margin-bottom: 3rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 2rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1.5rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 1rem;
    margin-bottom: 1rem;
  }
`;

const NPSContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  position: relative;
  padding: 1rem;
  gap: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    padding: 0.5rem;
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    padding: 0.5rem;
    gap: 0.5rem;
  }
`;

const NPSGaugeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 50%;
  height: 100%;
  transform: translateY(-30px);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    transform: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    transform: none;
  }
`;

const NPSGaugeContainer = styled.div`
  position: relative;
  width: 400px;
  height: 300px;
  margin-bottom: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 320px;
    height: 240px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 280px;
    height: 220px;
    margin-bottom: 0.5rem;
  }
`;

const NPSLabels = styled.div`
  display: flex;
  flex-direction: row;
  gap: 2rem;
  justify-content: center;
  align-items: center;
  width: 50%;
  height: 100%;
  padding: 2rem;
  transform: translateY(-30px);

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    transform: none;
    padding: 1rem;
    gap: 1rem;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
    transform: none;
    padding: 0.5rem;
    gap: 0.5rem;
    flex-wrap: wrap;
  }
`;

const NPSLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  text-align: center;

  .percentage {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 0.25rem;
  }

  .label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
  }
`;

const NPSLabelCircle = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ color }) => color}15;
  border: 2px solid ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const NPSChartCard = styled(ChartCard)`
  height: 580px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 560px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 530px;
  }
`;

const ResponsiveOrigemChart = styled(ChartCard)`
  height: 400px;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.border.light};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: 450px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    height: 500px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    height: 650px;
  }
`;

// Tipos removidos - usando interfaces inline quando necessário

// Configuração dos tipos de motivos de desligamento
const motivosDesligamentosCheckbox = [
  {
    id: 'pedido_demissao',
    label: 'Pedido de demissão',
    shortLabel: 'Pedido de demissão',
    type: 'voluntario',
  },
  {
    id: 'pedido_demissao_experimental',
    label: 'Pedido de demissão durante o período experimental',
    shortLabel: 'P. demissão no período experimental',
    type: 'voluntario',
  },
  {
    id: 'demissao_sem_justa_causa',
    label: 'Demissão sem justa causa',
    shortLabel: 'Demissão sem justa causa',
    type: 'involuntario',
  },
  {
    id: 'desligado_experimental',
    label: 'Desligado no período experimental',
    shortLabel: 'Desligado no período experimental',
    type: 'involuntario',
  },
];

export const Desligamentos: React.FC = () => {
  const [filters, setFilters] = useState({
    empresa: 'todos',
    filial: 'todos',
    departamento: 'todos',
    lider: 'todos',
    origemDesligamentos: 'todos',
  });

  const [selectedMotivos, setSelectedMotivos] = useState({
    pedido_demissao: true,
    pedido_demissao_experimental: false,
    demissao_sem_justa_causa: false,
    desligado_experimental: false,
  });

  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Obter dados do JSON baseado nos filtros
  const desligamentosData = useMemo(() => {
    const baseData = desligamentosDataJson.EMPRESA_DEMONSTRACAO;
    return (
      baseData[filters.empresa as keyof typeof baseData]?.[
        filters.filial as keyof (typeof baseData)[keyof typeof baseData]
      ]?.[
        filters.departamento as keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]]
      ]?.[
        filters.lider as keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]][keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]]]
      ]?.[
        filters.origemDesligamentos as keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]][keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]]][keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]][keyof (typeof baseData)[keyof typeof baseData][keyof (typeof baseData)[keyof typeof baseData]]]]
      ] || baseData.todos.todos.todos.todos.todos
    );
  }, [filters]);

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleMotivoToggle = (motivoId: string) => {
    setSelectedMotivos((prev) => ({
      ...prev,
      [motivoId]: !prev[motivoId as keyof typeof prev],
    }));
  };

  const handleBuscar = () => {
    console.log('Filtros aplicados:', filters);
  };

  const handleLimparFiltros = () => {
    setFilters({
      empresa: 'todos',
      filial: 'todos',
      departamento: 'todos',
      lider: 'todos',
      origemDesligamentos: 'todos',
    });
    setSelectedMotivos({
      pedido_demissao: true,
      pedido_demissao_experimental: false,
      demissao_sem_justa_causa: false,
      desligado_experimental: false,
    });
  };

  // Lógica para determinar quais motivos mostrar baseado nos filtros selecionados
  const getFilteredMotivos = () => {
    const selectedTypes = motivosDesligamentosCheckbox
      .filter(
        (motivo) => selectedMotivos[motivo.id as keyof typeof selectedMotivos]
      )
      .map((motivo) => motivo.id);

    let motivosToShow: { name: string; value: number; color: string }[] = [];

    selectedTypes.forEach((type) => {
      if (
        desligamentosData.motivosDesligamentos[
          type as keyof typeof desligamentosData.motivosDesligamentos
        ]
      ) {
        motivosToShow = [
          ...motivosToShow,
          ...desligamentosData.motivosDesligamentos[
            type as keyof typeof desligamentosData.motivosDesligamentos
          ],
        ];
      }
    });

    // Se nenhum tipo selecionado, mostrar dados padrão
    if (motivosToShow.length === 0) {
      motivosToShow = desligamentosData.motivosDesligamentos.pedido_demissao;
    }

    return {
      name: '',
      color: '#ffffff',
      children: motivosToShow,
    };
  };

  const filteredMotivos = getFilteredMotivos();

  return (
    <DesligamentosContainer>
      <ContentWrapper>
        <PageHeader>
          <PageTitle>Desligamentos</PageTitle>
        </PageHeader>

        {/* Filtros */}
        <FiltersContainer>
          <FiltersRow>
            <div>
              <FilterLabel>Empresa</FilterLabel>
              <FilterSelect
                value={filters.empresa}
                onChange={(e) => handleFilterChange('empresa', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="empresa1">Empresa 1</option>
                <option value="empresa2">Empresa 2</option>
              </FilterSelect>
            </div>

            <div>
              <FilterLabel>Filial</FilterLabel>
              <FilterSelect
                value={filters.filial}
                onChange={(e) => handleFilterChange('filial', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="filial1">Filial 1</option>
                <option value="filial2">Filial 2</option>
              </FilterSelect>
            </div>

            <div>
              <FilterLabel>Departamento</FilterLabel>
              <FilterSelect
                value={filters.departamento}
                onChange={(e) =>
                  handleFilterChange('departamento', e.target.value)
                }
              >
                <option value="todos">Todos</option>
                <option value="ti">TI</option>
                <option value="rh">RH</option>
                <option value="vendas">Vendas</option>
              </FilterSelect>
            </div>

            <div>
              <FilterLabel>Líder</FilterLabel>
              <FilterSelect
                value={filters.lider}
                onChange={(e) => handleFilterChange('lider', e.target.value)}
              >
                <option value="todos">Todos</option>
                <option value="lider1">Líder 1</option>
                <option value="lider2">Líder 2</option>
              </FilterSelect>
            </div>

            <div>
              <FilterLabel>Origem dos desligamentos</FilterLabel>
              <FilterSelect
                value={filters.origemDesligamentos}
                onChange={(e) =>
                  handleFilterChange('origemDesligamentos', e.target.value)
                }
              >
                <option value="todos">Todos</option>
                <option value="pedido_demissao">Pedido de demissão</option>
                <option value="demissao_justa_causa">
                  Demissão com justa causa
                </option>
              </FilterSelect>
            </div>
          </FiltersRow>

          <ActionsRow>
            <FilterActions>
              <ActionButton variant="primary" onClick={handleBuscar}>
                <Search size={16} />
                Buscar
              </ActionButton>
              <ActionButton onClick={handleLimparFiltros}>
                <Filter size={16} />
                Limpar filtros
              </ActionButton>
            </FilterActions>
          </ActionsRow>
        </FiltersContainer>

        {/* Layout Principal */}
        <MainLayoutGrid>
          <LeftColumn>
            <ResponsiveOrigemChart>
              <ChartTitle>
                <BarChart3 size={20} />
                Origem dos desligamentos
              </ChartTitle>
              <ChartContainer>
                <div style={{ width: '100%', height: '100%' }}>
                  <ResponsivePie
                    data={desligamentosData.origemDesligamentos}
                    margin={{
                      top: 20,
                      right:
                        screenWidth <= 768
                          ? 20
                          : screenWidth <= 1024
                          ? 200
                          : 280,
                      bottom: screenWidth <= 768 ? 160 : 20,
                      left: screenWidth <= 768 ? 20 : -40,
                    }}
                    innerRadius={0.5}
                    padAngle={0.7}
                    cornerRadius={3}
                    activeOuterRadiusOffset={8}
                    colors={{ datum: 'data.color' }}
                    borderWidth={1}
                    borderColor={{
                      from: 'color',
                      modifiers: [['darker', 0.2]],
                    }}
                    enableArcLinkLabels={false}
                    enableArcLabels={true}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor="#ffffff"
                    arcLabel={(d) => `${d.value}%`}
                    tooltip={({ datum }) => (
                      <div
                        style={{
                          background: 'white',
                          padding: '12px 16px',
                          border: '1px solid #E2E8F0',
                          borderRadius: '8px',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                          minWidth: '280px',
                          maxWidth: '320px',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                        }}
                      >
                        <div
                          style={{
                            color: datum.color,
                            fontWeight: 'bold',
                            fontSize: '14px',
                            lineHeight: '1.4',
                            marginBottom: '4px',
                          }}
                        >
                          {datum.label}
                        </div>
                        <div
                          style={{
                            color: '#64748B',
                            fontSize: '13px',
                            fontWeight: '500',
                          }}
                        >
                          {datum.value}%
                        </div>
                      </div>
                    )}
                    theme={chartTheme}
                    legends={[
                      {
                        anchor: screenWidth <= 768 ? 'bottom' : 'right',
                        direction: screenWidth <= 768 ? 'column' : 'column',
                        justify: false,
                        translateX: screenWidth <= 768 ? 0 : 180,
                        translateY: screenWidth <= 768 ? 120 : 0,
                        itemsSpacing: screenWidth <= 768 ? 6 : 8,
                        itemWidth: screenWidth <= 768 ? 280 : 220,
                        itemHeight: screenWidth <= 768 ? 18 : 20,
                        itemTextColor: '#64748B',
                        itemDirection: 'left-to-right',
                        itemOpacity: 1,
                        symbolSize: screenWidth <= 768 ? 10 : 12,
                        symbolShape: 'circle',
                      },
                    ]}
                  />
                </div>
              </ChartContainer>
            </ResponsiveOrigemChart>

            <StatsSubGrid>
              <StatsCard
                title="Turnover"
                value={`${desligamentosData.turnover.percentage}%`}
                subtitle={`↓${Math.abs(desligamentosData.turnover.trend)}% ${
                  desligamentosData.turnover.description
                }`}
                icon={<TrendingUp size={24} />}
                color="#8B5CF6"
              />
              <StatsCard
                title="Colaboradores desligados nos últimos 90 dias"
                value={desligamentosData.colaboradores.count.toString()}
                subtitle={`↑${desligamentosData.colaboradores.trend}% ${desligamentosData.colaboradores.description}`}
                icon={<Users size={24} />}
                color="#06B6D4"
              />
            </StatsSubGrid>
          </LeftColumn>

          <RightColumn>
            <ExtraLargeChartCard>
              <ChartTitle>
                <BarChart3 size={20} />
                Principais motivos de desligamentos
              </ChartTitle>
              <div style={{ padding: '0 1.5rem', marginTop: '1.5rem' }}>
                <CheckboxContainer>
                  {motivosDesligamentosCheckbox.map((motivo) => (
                    <CheckboxWrapper key={motivo.id}>
                      <Checkbox
                        type="checkbox"
                        id={motivo.id}
                        checked={
                          selectedMotivos[
                            motivo.id as keyof typeof selectedMotivos
                          ]
                        }
                        onChange={() => handleMotivoToggle(motivo.id)}
                      />
                      <CheckboxLabel htmlFor={motivo.id}>
                        {motivo.shortLabel}
                      </CheckboxLabel>
                    </CheckboxWrapper>
                  ))}
                </CheckboxContainer>
              </div>
              <ChartContainer>
                <ResponsiveCirclePacking
                  data={filteredMotivos}
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                  id="name"
                  value="value"
                  colors={{ datum: 'data.color' }}
                  padding={6}
                  enableLabels={true}
                  labelTextColor="#ffffff"
                  labelsSkipRadius={22}
                  borderWidth={2}
                  borderColor={{ from: 'color', modifiers: [['darker', 0.3]] }}
                  label={(node: any) => {
                    const radius = node.r || 20;
                    const maxLength = Math.max(6, Math.floor(radius / 1.8));
                    return node.data.name.length > maxLength
                      ? node.data.name.substring(0, maxLength) + '...'
                      : node.data.name;
                  }}
                  theme={chartTheme}
                  tooltip={(props: any) => (
                    <div
                      style={{
                        background: 'white',
                        padding: '12px 16px',
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        color: '#1E293B',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                    >
                      <strong>{props.data?.name}</strong>: {props.data?.value}%
                    </div>
                  )}
                />
              </ChartContainer>
            </ExtraLargeChartCard>
          </RightColumn>
        </MainLayoutGrid>

        {/* Seção eNPS */}
        <NPSSection>
          {/* eNPS com Gauge */}
          <NPSChartCard>
            <ChartTitle>
              <Smile size={24} />
              NPS
            </ChartTitle>
            <ChartContainer style={{ minHeight: '480px' }}>
              <NPSContainer>
                <NPSGaugeSection>
                  <NPSGaugeContainer>
                    <ResponsivePie
                      data={[
                        { id: 'detratores', value: 33.33, color: '#dc2626' }, // Detratores
                        { id: 'neutros', value: 33.33, color: '#eab308' }, // Neutros
                        { id: 'promotores', value: 33.34, color: '#10b981' }, // Promotores
                      ]}
                      margin={{
                        top: screenWidth <= 768 ? 60 : 80,
                        right: screenWidth <= 768 ? 40 : 60,
                        bottom: screenWidth <= 768 ? 80 : 100,
                        left: screenWidth <= 768 ? 40 : 60,
                      }}
                      startAngle={-90}
                      endAngle={90}
                      innerRadius={0.65}
                      padAngle={0}
                      cornerRadius={0}
                      activeOuterRadiusOffset={0}
                      colors={{ datum: 'data.color' }}
                      borderWidth={2}
                      borderColor="#ffffff"
                      enableArcLabels={false}
                      enableArcLinkLabels={false}
                      theme={{
                        ...chartTheme,
                        background: 'transparent',
                      }}
                      animate={true}
                      motionConfig="gentle"
                      layers={[
                        'arcs',
                        ({ centerX, centerY, radius }) => (
                          <g>
                            {/* Score abaixo do gauge */}
                            <text
                              x={centerX}
                              y={centerY + 40}
                              textAnchor="middle"
                              dominantBaseline="central"
                              style={{
                                fontSize: '3.5rem',
                                fontWeight: 800,
                                fill: '#1e293b',
                              }}
                            >
                              {desligamentosData.nps.score}
                            </text>

                            {/* Ponteiro - apontando para posição baseada no score */}
                            <g transform={`translate(${centerX}, ${centerY})`}>
                              <line
                                x1="0"
                                y1="0"
                                x2={
                                  Math.cos(
                                    ((-180 +
                                      ((desligamentosData.nps.score * 10) /
                                        100) *
                                        180) *
                                      Math.PI) /
                                      180
                                  ) *
                                  (radius * 0.7)
                                }
                                y2={
                                  Math.sin(
                                    ((-180 +
                                      ((desligamentosData.nps.score * 10) /
                                        100) *
                                        180) *
                                      Math.PI) /
                                      180
                                  ) *
                                  (radius * 0.7)
                                }
                                stroke="#1e293b"
                                strokeWidth="4"
                                strokeLinecap="round"
                              />
                              <circle cx="0" cy="0" r="6" fill="#1e293b" />
                            </g>

                            {/* Ícones posicionados para 3 categorias - no meio de cada barra */}
                            {/* Detratores (esquerda) - centro em -150° (meio da barra vermelha) */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 25) * Math.cos((-150 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 25) * Math.sin((-150 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="18"
                                fill="#fee2e2"
                                stroke="#dc2626"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-12"
                                y="-12"
                                width="24"
                                height="24"
                              >
                                <Angry size={24} color="#dc2626" />
                              </foreignObject>
                            </g>

                            {/* Neutros (centro) - centro em -90° (meio da barra amarela) */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 35) * Math.cos((-90 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 35) * Math.sin((-90 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="18"
                                fill="#fef3c7"
                                stroke="#eab308"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-12"
                                y="-12"
                                width="24"
                                height="24"
                              >
                                <Meh size={24} color="#eab308" />
                              </foreignObject>
                            </g>

                            {/* Promotores (direita) - centro em -30° (meio da barra verde) */}
                            <g
                              transform={`translate(${
                                centerX +
                                (radius + 25) * Math.cos((-30 * Math.PI) / 180)
                              }, ${
                                centerY +
                                (radius + 25) * Math.sin((-30 * Math.PI) / 180)
                              })`}
                            >
                              <circle
                                cx="0"
                                cy="0"
                                r="18"
                                fill="#dcfce7"
                                stroke="#10b981"
                                strokeWidth="2"
                              />
                              <foreignObject
                                x="-12"
                                y="-12"
                                width="24"
                                height="24"
                              >
                                <Smile size={24} color="#10b981" />
                              </foreignObject>
                            </g>
                          </g>
                        ),
                      ]}
                    />
                  </NPSGaugeContainer>
                </NPSGaugeSection>

                <NPSLabels>
                  <NPSLabel>
                    <NPSLabelCircle color="#10b981">
                      <Smile size={32} color="#10b981" />
                    </NPSLabelCircle>
                    <div className="percentage">
                      {desligamentosData.nps.promotores}%
                    </div>
                    <div className="label">Promotores</div>
                  </NPSLabel>
                  <NPSLabel>
                    <NPSLabelCircle color="#eab308">
                      <Meh size={32} color="#eab308" />
                    </NPSLabelCircle>
                    <div className="percentage">
                      {desligamentosData.nps.neutros}%
                    </div>
                    <div className="label">Neutros</div>
                  </NPSLabel>
                  <NPSLabel>
                    <NPSLabelCircle color="#dc2626">
                      <Angry size={32} color="#dc2626" />
                    </NPSLabelCircle>
                    <div className="percentage">
                      {desligamentosData.nps.detratores}%
                    </div>
                    <div className="label">Detratores</div>
                  </NPSLabel>
                </NPSLabels>
              </NPSContainer>
            </ChartContainer>
          </NPSChartCard>

          {/* Gráfico NPS da imagem */}
          <NPSChartCard>
            <ChartTitle>NPS</ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={desligamentosData.npsData}
                margin={{ top: 40, right: 40, bottom: 180, left: 40 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: 'data.color' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                arcLabel={(d) => `${d.value}%`}
                theme={chartTheme}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 100,
                    itemsSpacing: 4,
                    itemWidth: 120,
                    itemHeight: 14,
                    itemTextColor: '#64748B',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 10,
                    symbolShape: 'circle',
                  },
                ]}
                tooltip={({ datum }) => (
                  <div
                    style={{
                      background: 'white',
                      padding: '12px 16px',
                      border: '1px solid #E2E8F0',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                      color: '#1E293B',
                      fontSize: '14px',
                      fontWeight: '500',
                    }}
                  >
                    <div
                      style={{
                        color: datum.color,
                        fontWeight: 'bold',
                        marginBottom: '4px',
                      }}
                    >
                      {datum.label}
                    </div>
                    <div>{datum.value}%</div>
                  </div>
                )}
              />
            </ChartContainer>
          </NPSChartCard>
        </NPSSection>

        {/* Gráficos Inferiores */}
        <BottomChartsGrid>
          <ChartCard>
            <ChartTitle>
              <Settings size={20} />
              Gestão
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={desligamentosData.gestaoData}
                margin={{ top: 60, right: 60, bottom: 180, left: 60 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: 'data.color' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                arcLabel={(d) => `${d.value}%`}
                theme={chartTheme}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 160,
                    itemsSpacing: 4,
                    itemWidth: 120,
                    itemHeight: 14,
                    itemTextColor: '#64748B',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 10,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </ChartContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>
              <Server size={20} />
              Infraestrutura
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={desligamentosData.infraestruturaData}
                margin={{ top: 60, right: 60, bottom: 180, left: 60 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: 'data.color' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                arcLabel={(d) => `${d.value}%`}
                theme={chartTheme}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 160,
                    itemsSpacing: 4,
                    itemWidth: 120,
                    itemHeight: 14,
                    itemTextColor: '#64748B',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 10,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </ChartContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>
              <Building size={20} />
              Empresa
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={desligamentosData.empresaData}
                margin={{ top: 60, right: 60, bottom: 180, left: 60 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: 'data.color' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                arcLabel={(d) => `${d.value}%`}
                theme={chartTheme}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 160,
                    itemsSpacing: 4,
                    itemWidth: 120,
                    itemHeight: 14,
                    itemTextColor: '#64748B',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 10,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </ChartContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>
              <Target size={20} />
              Auto gestão
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={desligamentosData.autoGestaoData}
                margin={{ top: 60, right: 60, bottom: 180, left: 60 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: 'data.color' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                arcLabel={(d) => `${d.value}%`}
                theme={chartTheme}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 160,
                    itemsSpacing: 4,
                    itemWidth: 120,
                    itemHeight: 14,
                    itemTextColor: '#64748B',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 10,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </ChartContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>
              <FileText size={20} />
              Políticas de RH
            </ChartTitle>
            <ChartContainer>
              <ResponsivePie
                data={desligamentosData.politicasRHData}
                margin={{ top: 60, right: 60, bottom: 180, left: 60 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ datum: 'data.color' }}
                borderWidth={1}
                borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                enableArcLinkLabels={false}
                enableArcLabels={true}
                arcLabelsSkipAngle={10}
                arcLabelsTextColor="#ffffff"
                arcLabel={(d) => `${d.value}%`}
                theme={chartTheme}
                legends={[
                  {
                    anchor: 'bottom',
                    direction: 'column',
                    justify: false,
                    translateX: 0,
                    translateY: 160,
                    itemsSpacing: 4,
                    itemWidth: 120,
                    itemHeight: 14,
                    itemTextColor: '#64748B',
                    itemDirection: 'left-to-right',
                    itemOpacity: 1,
                    symbolSize: 10,
                    symbolShape: 'circle',
                  },
                ]}
              />
            </ChartContainer>
          </ChartCard>
        </BottomChartsGrid>

        {/* Gráfico de Principais Causas */}
        <CausesChartContainer>
          <LargeChartCard>
            <ChartTitle>
              <BarChart3 size={20} />
              Principais causas de desligamento
            </ChartTitle>
            <ChartContainer>
              <ResponsiveBar
                data={desligamentosData.principaisCausas}
                keys={['valor']}
                indexBy="causa"
                margin={{
                  top: screenWidth <= 768 ? 40 : 20,
                  right: screenWidth <= 768 ? 40 : 80,
                  bottom: 60,
                  left:
                    screenWidth <= 768 ? 200 : screenWidth <= 1024 ? 240 : 280,
                }}
                padding={screenWidth <= 768 ? 0.2 : 0.3}
                layout="horizontal"
                valueScale={{ type: 'linear' }}
                indexScale={{ type: 'band', round: true }}
                colors={['#8B5CF6']}
                borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                axisTop={
                  screenWidth <= 768
                    ? null
                    : {
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: 'Número de colaboradores',
                        legendPosition: 'middle',
                        legendOffset: -40,
                      }
                }
                axisRight={null}
                axisBottom={null}
                axisLeft={{
                  tickSize: 5,
                  tickPadding: 5,
                  tickRotation: 0,
                  legend: '',
                  legendPosition: 'middle',
                  legendOffset: 0,
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelTextColor="#ffffff"
                theme={chartTheme}
              />
            </ChartContainer>
          </LargeChartCard>
        </CausesChartContainer>
      </ContentWrapper>
    </DesligamentosContainer>
  );
};
